import React, { useState, useEffect } from 'react';
import {

  ChevronDown,
  Github,
  ExternalLink,
  Mail,
  Linkedin,
  Code,
  Gamepad2,
  Monitor,
  Calendar,
  MapPin,
  Download,
  Star,
  Award,
  Zap

} from 'lucide-react';

function App() {
  const [activeSection, setActiveSection] = useState('hero');
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    const handleScroll = () => {
      const sections = ['hero', 'about', 'projects', 'skills', 'experience', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const projects = [
    {
      title: "Custom Github Forgejo",
      description: "Self-hosted Git server using Forgejo, providing private repositories, issue tracking, and CI/CD capabilities. Configured with custom domain, SSL certificates, and automated backups.",
      tech: ["Forgejo", "Docker", "Linux Server", "Nginx", "SSL/TLS"],
      category: "Infrastructure",
      link: "https://forge.caesargaming.org",
      image: "🔧",
      featured: true
    },
    {
      title: "Pazarluk.mk",
      description: "A comprehensive price comparison platform for Macedonia, helping users find the best deals and cheapest prices across multiple retailers and categories.",
      tech: ["Python", "PostgreSQL", "Alpine.js", "Tailwind CSS", "Django"],
      category: "E-commerce",
      link: "https://pazarluk.mk",
      image: "💰",
      featured: true
    },
    {
      title: "Interactive Card Game",
      description: "A web-based card game with engaging gameplay mechanics and smooth user interface. Built with modern web technologies.",
      tech: ["HTML5", "CSS3", "JavaScript", "Canvas API"],
      category: "Gaming",
      link: "https://tt.caesargaming.org",
      image: "🎮",
      featured: true
    },
    {
      title: "Browser Tab Manager",
      description: "A browser extension designed to help users organize and manage multiple tabs efficiently with intuitive controls.",
      tech: ["JavaScript", "HTML5", "CSS3", "Chrome Extension API"],
      category: "Productivity",
      link: "https://tab.caesargaming.org",
      image: "🗂️",
      featured: true
    }
  ];

  const skills = {
    "Frontend Development": {
      skills: ["HTML5", "CSS3", "JavaScript", "React", "TypeScript", "Responsive Design", "UI/UX", "Tailwind CSS"],
      icon: <Monitor className="w-6 h-6" />
    },
    "Backend & Databases": {
      skills: ["Python", "Django", "Node.js", "PostgreSQL", "MySQL", "REST APIs", "PHP", "SQL"],
      icon: <Code className="w-6 h-6" />
    },
    "DevOps & Infrastructure": {
      skills: ["Docker", "Git", "CI/CD", "GitHub Actions", "Linux", "Nginx", "AWS", "Cloud Hosting", "Server Management"],
      icon: <Zap className="w-6 h-6" />
    },
    "Tools & Technologies": {
      skills: ["VS Code", "Chrome DevTools", "Figma", "Photoshop", "WordPress", "Shopify", "Unity Basics", "Canvas API", "Web Scraping"],
      icon: <Gamepad2 className="w-6 h-6" />
    }
  };

  const experience = [
    {
      title: "Frontend Developer",
      company: "Responsive UI Development",
      period: "2023 - Present",
      description: "Specialized in creating responsive user interfaces and interactive web experiences for gaming applications. Focused on modern JavaScript frameworks and user experience optimization.",
      achievements: [
        "Developed responsive gaming interfaces using React and vanilla JavaScript",
        "Implemented interactive UI components with smooth animations and transitions",
        "Created cross-browser compatible web applications with mobile-first design",
        "Built reusable component libraries for consistent user experiences",
        "Optimized frontend performance and implemented accessibility best practices"
      ],
      technologies: ["React", "JavaScript", "HTML5", "CSS3", "Sass", "Responsive Design"]
    },
    {
      title: "Backend Developer",
      company: "API & Database Systems",
      period: "2023 - Present",
      description: "Developed server-side applications and API. Focused on database design, API development, and server-side logic implementation.",
      achievements: [
        "Built RESTful API",
        "Designed database schemas for product and pricing data storage",
        "Created user authentication and authorization systems with JWT",
        "Implemented data validation, error handling, and logging systems",
        "Developed Python web scraping system for automated price collection"
      ],
      technologies: ["Node.js", "Express.js", "PostgresSQL", "SQLite", "JWT", "Python"]
    },
    {
      title: "DevOps Engineer",
      company: "Deployment & Infrastructure Management",
      period: "2023 - Present",
      description: "Managed deployment pipelines and infrastructure for web applications. Focused on containerization, continuous integration, and automated deployment processes.",
      achievements: [
        "Set up CI/CD pipelines using Git workflows for automated testing and deployment",
        "Containerized applications using Docker for consistent development environments",
        "Deployed applications to cloud platforms with monitoring and logging",
        "Implemented version control strategies and branching workflows",
        "Automated build processes and established development environment standards"
      ],
      technologies: ["Docker", "Git", "GitHub Actions", "Linux", "Nginx", "Cloud Hosting"]
    },
    {
      title: "Programmer & Modder",
      company: "Programming Fundamentals",
      period: "2023 - 2023",
      description: "Intensive self-directed learning period focused on mastering programming fundamentals and full-stack development concepts through structured courses and practical projects.",
      achievements: [
        "Completed comprehensive full-stack development bootcamp curriculum",
        "Mastered core programming concepts in JavaScript, Python, and web technologies",
        "Built 30+ practice projects covering frontend, backend, and database integration",
      ],
      technologies: ["JavaScript", "Python", "HTML", "CSS", "Git", "SQL"]
    }
  ];

  const stats = [
    { number: "2+", label: "Years Learning", icon: <Calendar className="w-5 h-5" /> },
    { number: "20+", label: "Projects Built", icon: <Star className="w-5 h-5" /> },
    { number: "2000+", label: "Hours Coded", icon: <Code className="w-5 h-5" /> },
    { number: "∞", label: "Passion for Tech", icon: <Award className="w-5 h-5" /> }
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-hidden">

      {/* Animated Background */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)`
        }}
      />


      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-2">
          <div className="flex justify-between items-center">
            {/* Left side - Logo */}
            <div
              className="flex items-center cursor-pointer group"
              onClick={() => scrollToSection('hero')}
            >
              <div
                className="text-1xl cursor-pointer -mt-4"
                onClick={() => scrollToSection('hero')}
              >
                <span className="bg-gradient-to-r from-orange-500 to-yellow-400 bg-clip-text text-transparent tracking-widest -mb-4">

                </span>
              </div>

              <div className="w-16 h-16 rounded-full p-0.5 hover:scale-110 transition-transform duration-300">
                <div className="w-full h-full rounded-full bg-slate-900">
                  <img
                    src="/logocircle3.png"
                    alt="JSNK Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div
                className="text-1xl font-bold cursor-pointer mt-4"
                onClick={() => scrollToSection('hero')}
              >
                <span className="bg-gradient-to-r from-orange-500 to-yellow-400 bg-clip-text text-transparent tracking-widest">
                  Caesar<br />&nbsp;&nbsp;Gaming
                </span>
              </div>
            </div>

            {/* Center - Desktop Navigation */}
            <div className="hidden md:flex space-x-8 absolute left-1/2 transform -translate-x-1/2">
              {[
                { name: 'About', id: 'about' },
                { name: 'Projects', id: 'projects' },
                { name: 'Skills', id: 'skills' },
                { name: 'Experience', id: 'experience' },
                { name: 'Contact', id: 'contact' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`transition-colors duration-300 hover:text-purple-400 ${activeSection === item.id ? 'text-purple-400' : 'text-white'
                    }`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* Right side - Mobile Menu and Rays */}
            <div className="flex items-center space-x-4">
              {/* Mobile Hamburger Button - in middle */}
              <button
                className="md:hidden text-white focus:outline-none z-60"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <div className="w-6 h-6 flex flex-col justify-center items-center">
                  <span className={`block w-6 h-0.5 bg-white transition-transform duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                  <span className={`block w-6 h-0.5 bg-white mt-1 transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 bg-white mt-1 transition-transform duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
                </div>
              </button>

              {/* Animated Rays - after hamburger */}
              <div className="relative w-12 h-12 animate-spin" style={{ animationDuration: '20s' }}>
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-0.5 h-4 bg-gradient-to-t from-yellow-400 to-orange-300 rounded-full opacity-60"
                    style={{
                      transform: `rotate(${i * 45}deg)`,
                      transformOrigin: '50% 24px',
                      top: '-8px',
                      left: '50%',
                      marginLeft: '-1px'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          <div className={`md:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            } overflow-hidden`}>
            <div className="pt-4 pb-6 space-y-4 text-center">
              {[
                { name: 'About', id: 'about' },
                { name: 'Projects', id: 'projects' },
                { name: 'Skills', id: 'skills' },
                { name: 'Experience', id: 'experience' },
                { name: 'Contact', id: 'contact' }
              ].map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => {
                    scrollToSection(item.id);
                    setIsMenuOpen(false);
                  }}
                  className={`block w-full text-lg py-2 transition-all duration-300 hover:text-purple-400 transform ${isMenuOpen ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                    } ${activeSection === item.id ? 'text-purple-400' : 'text-white'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center justify-center relative pt-24">
        <div className={`text-center transform transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="mb-6">
            <div className="w-32 h-32 md:w-48 md:h-48 mx-auto rounded-full bg-gradient-to-r from-purple-500 to-pink-500 p-1 mb-6 mt-4 animate-float">
              <img
                src="/profile-photo.jpg"
                alt="Caesar Gaming - Full-stack Developer and DevOps"
                className="w-full h-full rounded-full object-cover border-2 border-white/20"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-4xl" style={{ display: 'none' }}>
                🎮
              </div>
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
            Caesar Gaming
          </h1>
          <h2 className="text-xl md:text-2xl text-purple-300 mb-6 font-medium">
            Full-Stack Developer, DevOps & Gaming Enthusiast
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto px-4 leading-relaxed">
            Passionate about creating engaging web experiences and interactive applications.
            Currently learning and building projects that combine technology with creativity.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto mb-8 px-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center mb-2 text-purple-400">
                  {stat.icon}
                </div>
                <div className="text-2xl font-bold text-white">{stat.number}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6 mb-12 px-4">
            <a
              href="mailto:contact@caesargaming.org"
              className="flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              <Mail size={20} />
              <span>Get In Touch</span>
            </a>

            <a
              href="/JSNK-CV.pdf"
              download="JSNK-CV.pdf"
              className="flex items-center justify-center space-x-2 border border-purple-400 hover:bg-purple-400/10 px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg"
            >
              <Download size={20} />
              <span>Download CV</span>
            </a>

          </div>
          <button
            onClick={() => scrollToSection('about')}
            className="animate-bounce hover:text-purple-300 transition-colors"
          >
            <ChevronDown size={32} className="text-purple-400" />
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            About Me
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-purple-300">
                Learning & Creating
              </h3>
              <p className="text-gray-300 leading-relaxed">
                I'm a passionate Full-Stack Developer and DevOps Enthusiast with a growing interest in web technologies and game development.
                My journey started with curiosity about how websites work, and has evolved into hands-on learning through building real projects.
              </p>
              <p className="text-gray-300 leading-relaxed">
                I enjoy problem-solving and creating interactive experiences that users find engaging. From simple web games to useful browser tools,
                I'm constantly exploring new technologies and improving my skills through practical application.
              </p>
              <p className="text-gray-300 leading-relaxed">
                I am from Denmark, Based in Skopje, North Macedonia, I'm eager to collaborate on projects that challenge me to grow while contributing meaningful value.
              </p>
            </div>
            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10">
                <h4 className="text-lg font-semibold text-purple-300 mb-4">Quick Facts</h4>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Calendar className="text-purple-400 flex-shrink-0" size={20} />
                    <span>Native: Danish, Denmark</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-300">
                    <MapPin className="text-purple-400 flex-shrink-0" size={20} />
                    <span>Current: Skopje, North Macedonia</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Calendar className="text-purple-400 flex-shrink-0" size={20} />
                    <span>Available for Opportunities</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Code className="text-purple-400 flex-shrink-0" size={20} />
                    <span>Full-Stack Development</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Code className="text-purple-400 flex-shrink-0" size={20} />
                    <span>DevOps</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-300">
                    <Gamepad2 className="text-purple-400 flex-shrink-0" size={20} />
                    <span>Gaming Projects</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Featured Projects
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl card-hover ${project.featured ? 'md:col-span-1' : ''
                  }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-4xl">{project.image}</div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                      {project.category}
                    </span>
                    {project.featured && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{project.title}</h3>
                <p className="text-gray-300 mb-4 leading-relaxed">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="text-xs bg-gray-700/50 text-gray-300 px-2 py-1 rounded border border-gray-600/50"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex space-x-4">
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <ExternalLink size={16} />
                    <span>Live Demo</span>
                  </a>
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    <Github size={16} />
                    <span>Code</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Skills & Technologies
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.entries(skills).map(([category, data], index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-purple-400">
                    {data.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-purple-300">{category}</h3>
                </div>
                <div className="space-y-3">
                  {data.skills.map((skill, skillIndex) => (
                    <div
                      key={skillIndex}
                      className="text-gray-300 hover:text-white transition-colors cursor-default py-1 px-2 rounded hover:bg-white/5"
                    >
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Experience & Roles
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {experience.map((exp, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">{exp.title}</h3>
                    <h4 className="text-md text-purple-300 mb-2">{exp.company}</h4>
                  </div>
                  <span className="text-gray-400 bg-gray-700/50 px-2 py-1 rounded text-sm">{exp.period}</span>
                </div>
                <p className="text-gray-300 mb-4 text-sm leading-relaxed">{exp.description}</p>
                <div className="mb-4">
                  <h5 className="text-purple-300 font-medium mb-2 text-sm">Key Achievements:</h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
                    {exp.achievements.map((achievement, achIndex) => (
                      <li key={achIndex}>{achievement}</li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-wrap gap-1">
                  {exp.technologies.map((tech, techIndex) => (
                    <span key={techIndex} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Let's Connect
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-12 px-4 leading-relaxed">
            I'm always interested in learning opportunities, collaboration, and connecting with fellow developers.
            Feel free to reach out if you'd like to discuss projects or just say hello!
          </p>
          <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-8 mb-12 px-4">
            <a
              href="https://www.linkedin.com/in/jonas-sch%C3%BCtt-nielsen-888ba6195/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-3 border border-purple-400 hover:bg-purple-400/10 px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-lg"
            >
              <Linkedin size={24} />
              <span className="text-lg">LinkedIn</span>
            </a>
          </div>
          <div className="flex justify-center space-x-6 text-gray-400">
            <a
              href="https://github.com/desmer11"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-purple-400 transition-colors transform hover:scale-110"
            >
              <Github size={24} />
            </a>
            <a
              href="https://caesargaming.org"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-purple-400 transition-colors transform hover:scale-110"
            >
              <Monitor size={24} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10 bg-black/30">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">
            © 2025 Caesar Gaming. Built with React and a passion for learning.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;